import { isCountryLevel } from "../routes";

/**
 * Země fotky a hierarchie destinací (vize podnety-tymu-2026-10, F7.1, řez 03 Kontrakt bod 4;
 * do sdíleného balíčku přesunuto v řezu 05, ať okno výběru fotek v adminu odvodí zemi
 * editované destinace týmž pravidlem jako API).
 *
 * Fotka patří k destinacím ZEMSKÉ ÚROVNĚ (`isCountryLevel`: země bez rodiče, nebo místo se
 * `showAsCountry`). Dvě otázky nad stromem, obě BEZ ohledu na publikaci (knihovna je
 * redakční nástroj, ne veřejný výpis):
 *
 * - `nearestCountryLevelId` — ke které zemi patří místo (Ubud → Bali, Bangkok → Thajsko);
 * - `countryLevelSubtreeIds` — které země zahrne filtr země (Indonésie → Indonésie, Bali).
 *
 * Oba průchody stropuje `MAX_HIERARCHY_DEPTH` (pojistka proti cyklu v datech), takže cyklus
 * nezacyklí požadavek.
 *
 * @prochazeni-predku JINÁ OTÁZKA než roll-up i osa adres. `publishedAncestorIds` sbírá
 * PUBLIKOVANÉ předky a na nepublikovaném se zastaví; `countrySlugOf` hledá zemského předka,
 * ale nepublikovaná země mu adresu srazí na plochou. Země fotky publikaci nezná: fotka
 * z Indonésie, která ještě nemá obsah, k Indonésii patří taky. Průchod se proto zastaví
 * na prvním předkovi zemské úrovně bez ohledu na `isPublished`.
 */

/**
 * Strop průchodu hierarchií. Katalog je tříúrovňový (země → povýšená lokalita → místo),
 * takže reálně stačí dva skoky; strop je pojistka proti cyklu v datech, který by jinak
 * zacyklil KAŽDÝ požadavek na výpis. Táž role jako `MAX_DEPTH` v `apps/api/src/lib/destinationPath.ts`.
 * Čtenáři v API ho berou z `apps/api/src/lib/destinationRollup.ts` (re-export).
 */
export const MAX_HIERARCHY_DEPTH = 8;

export interface CountryNode {
  id: string;
  parentId: string | null;
  showAsCountry: boolean | null;
}

/** Destinace zemské úrovně sama, jinak nejbližší předek zemské úrovně (`isCountryLevel`),
 *  bez ohledu na publikaci; do hloubky `MAX_HIERARCHY_DEPTH`; jinak null. */
export function nearestCountryLevelId(
  id: string,
  byId: ReadonlyMap<string, CountryNode>,
): string | null {
  let node = byId.get(id);
  for (let depth = 0; node && depth <= MAX_HIERARCHY_DEPTH; depth += 1) {
    if (isCountryLevel(node)) return node.id;
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return null;
}

/**
 * Destinace zemské úrovně: ta, jejíž nejbližší destinace zemské úrovně je ona sama
 * (`nearestCountryLevelId`). Jedno pravidlo pro filtr země knihovny i pro ověření zemí fotky
 * a průvodce v API (`countryLevelViolations`, `apps/api/src/lib/mediaCountries.ts`).
 */
export function isCountryLevelId(id: string, byId: ReadonlyMap<string, CountryNode>): boolean {
  return nearestCountryLevelId(id, byId) === id;
}

/** Země sama + všechny destinace zemské úrovně v jejím podstromu (Indonésie → Indonésie, Bali). */
export function countryLevelSubtreeIds(countryId: string, rows: readonly CountryNode[]): string[] {
  const children = new Map<string, CountryNode[]>();
  for (const row of rows) {
    if (!row.parentId) continue;
    const list = children.get(row.parentId);
    if (list) list.push(row);
    else children.set(row.parentId, [row]);
  }
  const out = [countryId];
  const seen = new Set(out);
  let frontier = [countryId];
  for (let depth = 0; frontier.length && depth < MAX_HIERARCHY_DEPTH; depth += 1) {
    const next: string[] = [];
    for (const parentId of frontier) {
      for (const child of children.get(parentId) ?? []) {
        if (seen.has(child.id)) continue;
        seen.add(child.id);
        next.push(child.id);
        if (isCountryLevel(child)) out.push(child.id);
      }
    }
    frontier = next;
  }
  return out;
}
